import { getImportCompanyUnitDetail, getImportCompanyUnitErrors } from "./articleImportCompanyUnits";
import { normalizeImportName, parseImportPrice } from "./articleImportModel";

const fields = { Barcode: "barcode", ArticleName: "name", Description: "description", GroupID: "group", CategoryID: "category",
  UnitID: "unit", VAT: "vat", LedgerID: "ledgerId", ArticleProduction: "articleProduction", PurchasePrice: "purchasePrice", SalesPrice: "salesPrice" };
const detailFields = { Barcode: "barcode", ArticleUnitID: "unitId", CompanyUnitID: "companyUnitId", SalesTypeID: "salesType", PackageQty: "quantity", SalesPrice: "salesPrice" };

export function getImportServerErrors(row, unitId, information = false) {
  const errors = {};
  (row.serverIssues || []).forEach(issue => {
    if ((issue.Severity === "Info") !== information) return;
    const child = issue.SalesOptionIndex != null;
    if (unitId != null ? !child || Number(issue.CompanyUnitID) !== unitId : child) return;
    const field = (unitId != null ? detailFields : fields)[issue.Field] || "validation";
    errors[field] = [errors[field], issue.Message].filter(Boolean).join(" ");
  });
  if (!information && unitId == null && row.serverIssues?.some(issue => issue.SalesOptionIndex != null && issue.Severity !== "Info"))
    errors.companyUnits = "Një nga njësitë ka gabime nga kontrolli në databazë. Hapeni për të parë arsyen.";
  if (information && unitId == null && row.serverIssues?.some(issue => issue.SalesOptionIndex != null && issue.Severity === "Info"))
    errors.companyUnits = "Një barkod i njësive organizative ekziston në databazë. Artikulli do të anashkalohet; hapni njësitë për hollësitë.";
  return errors;
}

export function toArticleImportValidationRow(row) {
  return { ExcelRow: row.excelRow, Barcode: row.barcode, ArticleName: row.name, Description: row.description,
    GroupID: row.resolved.group, CategoryID: row.resolved.category, UnitID: row.resolved.unit, VAT: row.resolved.vat,
    LedgerID: row.ledgerId, ArticleProduction: Number(row.articleProduction),
    PurchasePrice: row.resolved.purchasePrice, SalesPrice: row.resolved.salesPrice,
    SalesOptions: (row.companyUnitIds || []).map(unitId => {
      const detail = getImportCompanyUnitDetail(row, unitId);
      return { IsActive: detail.active, CompanyUnitID: unitId, Barcode: detail.barcode, ArticleUnitID: Number(detail.unitId), SalesTypeID: Number(detail.salesType),
        PackageQty: parseImportPrice(detail.quantity, null), SalesPrice: parseImportPrice(detail.salesPrice, null) };
    }) };
}

// One click, sequential batches. Per-row issues never interrupt subsequent batches.
export async function validateArticleImportBatches(items, unitIds, { signal, onProgress, validate } = {}) {
  const checkCancelled = () => { if (signal?.aborted) throw new Error("Kontrolli u anulua."); };
  const results = new Map();
  const owners = new Map();
  items.forEach(({ row }) => {
    const codes = [row.barcode, ...(row.companyUnitIds || []).map(id => getImportCompanyUnitDetail(row, id).barcode)];
    codes.forEach(code => {
      const key = normalizeImportName(code);
      if (!key) return;
      if (!owners.has(key)) owners.set(key, new Set());
      owners.get(key).add(row.id);
    });
  });
  let checked = 0;
  let batch = [];
  let details = 0;
  const total = items.length;
  const issue = (row, message, field = "Validation") => ({ ExcelRow: row.excelRow, Field: field, Message: message });
  async function send() {
    if (!batch.length) return;
    checkCancelled();
    const payload = batch.map(toArticleImportValidationRow);
    const response = await validate(payload, signal);
    checkCancelled();
    const excelRows = new Set(batch.map(row => row.excelRow));
    if (response?.CheckedRows !== batch.length || !Array.isArray(response.Issues) ||
        response.Issues.some(item => !excelRows.has(item.ExcelRow) || typeof item.Message !== "string" ||
          (item.SalesOptionIndex != null && (!Number.isInteger(item.SalesOptionIndex) || item.SalesOptionIndex < 0 ||
            payload.find(row => row.ExcelRow === item.ExcelRow)?.SalesOptions[item.SalesOptionIndex]?.CompanyUnitID !== item.CompanyUnitID))))
      throw new Error("Serveri ktheu rezultat validimi të pavlefshëm. Kontrolli nuk përfundoi.");
    batch.forEach(row => results.set(row.id, []));
    const byExcel = new Map(batch.map(row => [row.excelRow, row.id]));
    response.Issues.forEach(item => results.get(byExcel.get(item.ExcelRow)).push(item));
    checked += batch.length;
    batch = []; details = 0;
    onProgress?.({ checked, total });
  }
  try {
    for (const { row, errors } of items) {
      checkCancelled();
      const localErrors = Object.entries(errors);
      const ids = row.companyUnitIds || [];
      const childInvalid = ids.some(id => Object.keys(getImportCompanyUnitErrors(row, getImportCompanyUnitDetail(row, id), unitIds)).length);
      const duplicated = [row.barcode, ...ids.map(id => getImportCompanyUnitDetail(row, id).barcode)]
        .some(code => owners.get(normalizeImportName(code))?.size > 1);
      if (localErrors.length || childInvalid || duplicated || ids.length > 5000) {
        const messages = localErrors.map(([field, message]) => issue(row, message, Object.keys(fields).find(key => fields[key] === field) || "Validation"));
        if (childInvalid) messages.push(issue(row, "Korrigjoni gabimet në nënrreshtat e njësive organizative."));
        if (duplicated) messages.push(issue(row, "Barkodi përdoret nga disa artikuj në këtë import.", "Barcode"));
        if (ids.length > 5000) messages.push(issue(row, "Artikulli ka mbi 5000 opsione; zvogëloni njësitë për këtë artikull."));
        results.set(row.id, messages); checked++;
        if (checked % 500 === 0 || checked === total) onProgress?.({ checked, total });
        continue;
      }
      if (batch.length === 500 || details + ids.length > 5000) await send();
      batch.push(row); details += ids.length;
    }
    await send();
    return { results, checked, total, complete: true };
  } catch (error) {
    return { results, checked, total, complete: false, error: signal?.aborted ? "Kontrolli u anulua." : error.message };
  }
}
